import { useEffect, useState } from 'react'

import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import useMediaSourceViews from '@/actions/useMediaSourceViews'
import { useAppContext } from '@/AppContext'
import useMediaSourceActions from '@/functionalities/MediaSources/useMediaSourceActions'
import * as Constants from '@/services/Constants'
import useAnalytics from '@/services/useAnalytics'
import { useUserContext } from '@/UserContext'
import { useTranslation } from 'react-i18next'
import { queryCurrentMediaSelectionWithFallback } from '@/Logic'
import { env } from '@/services/EnvironmentVariables'
import { logger } from '@/services/Logger'
import { Card } from '../Card'
import MIDIIcon from '@/CustomIcons/MIDIIcon'
import { CardHeader } from '../CardHeader'
import { Tutorial } from '../Tutorial'
import { EnabledIndicator } from '@/components/EnabledIndicator'
import { Alert } from '@/UIComponents2/Alert'
import { Button } from '@/UIComponents2/Button'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { persistent } from '@/services/LocalStorage'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import { SimpleSelect } from '@/UIComponents2/Select/SimpleSelect'
import FirstTimeMidiOffPopup from '@/pages/FirstTimeMidiOffPopup'
import { MidiMeter } from '@/components/MidiMeter2'
import midiCoverJpg from '@/assets/tutorials/midi-cover.jpg'

type MidiSettingsProps = {
    inSession: boolean
}
export function MidiSettings(props: MidiSettingsProps) {
    const { t } = useTranslation()

    const [userState] = useUserContext()
    const [appState] = useAppContext()

    function isMidiEnabled() {
        const input = userState.mediaSelection.midiInput
        const hasSelection = input !== '' && input !== '_none' && input !== '_undef'
        const enabled = env.OS === 'win32' ? appState.appSettings.forceAccessMidiForWindows : true

        return hasSelection && enabled && props.inSession
    }

    useEffect(() => {
        logger.event({ page: 'session-settings/midi', action: 'enter' })
        return () => {
            logger.event({ page: 'session-settings/midi', action: 'exit' })
        }
    }, [])

    return (
        <div className="flex flex-col gap-[10px]">
            <Card className="md:p-[10px] flex flex-col gap-[10px]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px]">
                    <div className="flex flex-col pl-[20px] py-[15px] gap-7">
                        <div className="flex justify-between">
                            <CardHeader title="MIDI Devices" icon={MIDIIcon} />
                            <EnabledIndicator enabled={isMidiEnabled()} />
                        </div>
                        <CrossPlatformMidiSettings />
                    </div>
                    <Tutorial
                        coverImageUrl={midiCoverJpg}
                        title="Share MIDI"
                        source={
                            env.OS === 'win32'
                                ? 'https://museapp.sfo3.cdn.digitaloceanspaces.com/in-app-tutorials/midi-win.mp4'
                                : 'https://museapp.sfo3.cdn.digitaloceanspaces.com/in-app-tutorials/midi-mac.mp4'
                        }
                        sourceType="video/mp4"
                    />
                </div>
            </Card>
        </div>
    )
}

function CrossPlatformMidiSettings() {
    if (!env.IS_ELECTRON) {
        return (
            <div>
                <Alert variant="solid" level="warning">
                    Download the{' '}
                    <Button variant="link" className="text-black font-semibold">
                        Muse app
                    </Button>{' '}
                    to share MIDI
                </Alert>
            </div>
        )
    }
    if (env.OS === 'win32') {
        return <WindowsMidiSettings />
    }
    if (env.OS === 'darwin') {
        return <MacMidiSettings />
    }

    return (
        <div>
            <Alert variant="solid" level="danger">
                Unknown platform
            </Alert>
        </div>
    )
}

function MacMidiSettings() {
    return (
        <div className="flex flex-col gap-7">
            <MidiSendSelect />
            <SimpleSelect
                label="MIDI Receive"
                value={'default'}
                items={[{ value: 'default', label: 'Muse MIDI Receive' }]}
                disabled={true}
            />
        </div>
    )
}

function WindowsMidiSettings() {
    const [appState] = useAppContext()
    const disabled = !appState.appSettings.forceAccessMidiForWindows

    return (
        <div className="flex flex-col gap-7">
            <EnableMidiSwitch />
            <MidiSendSelect disabled={disabled} />
            <MidiPassthroughDeviceSelect disabled={disabled} />
            <ReceiveMidiDeviceSelect disabled={disabled} />
        </div>
    )
}

function EnableMidiSwitch() {
    const [appState, appDispatch] = useAppContext()
    const { logUserActivityWithContext } = useAnalytics()
    const { loadMidiSources } = useMediaSourceActions()
    const [isShowFirstTimeMidiOffPopup, setIsShowFirstTimeMidiOffPopup] = useState(false)

    return (
        <>
            <div className="py-[14px] px-4 border-muse-toplit shadow-glow rounded-[16px] bg-white/3">
                <Checkbox
                    label={'Enable MIDI'}
                    checked={appState.appSettings.forceAccessMidiForWindows}
                    onChange={(val) => {
                        appDispatch({
                            type: 'update_app_settings',
                            value: {
                                forceAccessMidiForWindows: val,
                            },
                        })
                        if (val) {
                            loadMidiSources()
                        } else {
                            if (!persistent.getBoolean(Constants.notifications_suppress_midi_off_notice)) {
                                setIsShowFirstTimeMidiOffPopup(true)
                            }
                        }
                        logUserActivityWithContext(AnalyticsEvents.CLICKED_ENABLE_MIDI_BUTTON, {
                            toggleState: !appState.appSettings.forceAccessMidiForWindows,
                        })
                    }}
                />
            </div>
            {isShowFirstTimeMidiOffPopup && (
                <FirstTimeMidiOffPopup show={true} onClose={() => setIsShowFirstTimeMidiOffPopup(false)} />
            )}
        </>
    )
}

function MidiSendSelect(props: { disabled?: boolean }) {
    const { t } = useTranslation()
    const [userState] = useUserContext()
    const { mediaSources } = useMediaSourceViews()
    const { setMediaSelection } = useMediaSelectionActions()
    const { loadMidiSources } = useMediaSourceActions()

    const selectedMidiDevice =
        mediaSources.midiInputs.filter((device) => device.id === userState.mediaSelection.midiInput)[0] || null

    const selectedValue = queryCurrentMediaSelectionWithFallback(
        userState.mediaSelection,
        userState.mediaSources,
        'midiInput'
    )

    const makeItems = () => {
        let items = [{ value: '_none', label: t('None') }]
        for (const source of mediaSources.midiInputs) {
            items.push({
                value: source.id,
                label: `${source.name} (${source.manufacturer})`,
            })
        }
        return items
    }
    const items = makeItems()

    return (
        <div className="grid grid-cols-[1fr_min-content] gap-2 items-end">
            <SimpleSelect
                label="Select Input"
                value={selectedValue === '_undef' ? '' : selectedValue}
                onOpenChange={(open) => open && loadMidiSources()}
                items={items}
                disabled={props.disabled}
                placeholder={t('Select MIDI Send')}
                onChange={(val) => {
                    setMediaSelection({ midiInput: val })
                }}
            />
            <MidiMeter midiInput={selectedMidiDevice} className="max-h-[46px]" />
        </div>
    )
}

function MidiPassthroughDeviceSelect(props: { disabled?: boolean }) {
    const { t } = useTranslation()
    const [userState] = useUserContext()
    const { mediaSources } = useMediaSourceViews()
    const { setMediaSelection } = useMediaSelectionActions()
    const { loadMidiSources } = useMediaSourceActions()

    const selectedValue = queryCurrentMediaSelectionWithFallback(
        userState.mediaSelection,
        userState.mediaSources,
        'midiPassthroughOutput'
    )

    const makeItems = () => {
        let items = [
            { value: '_undef', label: t('MIDI Passthrough') },
            { value: '_none', label: t('None') },
        ]
        for (const source of mediaSources.midiInputs) {
            items.push({
                value: source.id,
                label: `${source.name} (${source.manufacturer})`,
            })
        }
        return items
    }
    const items = makeItems()

    return (
        <SimpleSelect
            label="MIDI Passthrough"
            value={selectedValue}
            onOpenChange={(open) => open && loadMidiSources()}
            items={items}
            disabled={props.disabled}
            onChange={(val) => {
                setMediaSelection({ midiPassthroughOutput: val })
            }}
        />
    )
}

function ReceiveMidiDeviceSelect(props: { disabled?: boolean }) {
    const { t } = useTranslation()
    const [userState] = useUserContext()
    const { mediaSources } = useMediaSourceViews()
    const { setMediaSelection } = useMediaSelectionActions()

    const selectedValue = queryCurrentMediaSelectionWithFallback(
        userState.mediaSelection,
        userState.mediaSources,
        'midiOutput'
    )

    function makeItems() {
        let items = [
            { value: '_undef', label: t('Select MIDI Receive') },
            { value: '_none', label: t('None') },
        ]
        for (const source of mediaSources.midiOutputs) {
            items.push({
                value: source.id,
                label: `${source.name} (${source.manufacturer})`,
            })
        }
        return items
    }
    const items = makeItems()

    return (
        <SimpleSelect
            label="Select Output"
            value={selectedValue}
            onChange={(val) => {
                setMediaSelection({ midiOutput: val })
            }}
            disabled={props.disabled}
            items={items}
        />
    )
}
